<p-confirmdialog />

<div class="management-page">
    <app-breadcrumbs [model]="breadcrumbs" />
    <header class="overview-header">
        <div>
            <p class="overview-eyebrow">Administration</p>
            <h1>Users</h1>
            <p class="overview-description">Invite people, assign tenant roles, and manage accounts.</p>
        </div>
        <div class="overview-actions">
            <p-button icon="pi pi-refresh" [outlined]="true" ariaLabel="Refresh" pTooltip="Refresh" [disabled]="loading()" (onClick)="load()" />
            @if (isSuperAdmin()) {
                <p-button label="New User" icon="pi pi-user-plus" [outlined]="true" pTooltip="Create with a password (for users without email access)" (onClick)="openCreate()" />
            }
            <p-button label="Invite User" icon="pi pi-envelope" (onClick)="openInvite()" />
        </div>
    </header>

    <section class="management-panel" aria-label="Users">
        <div class="management-toolbar">
            <label class="management-search">
                <i class="pi pi-search" aria-hidden="true"></i>
                <input pInputText type="search" aria-label="Search users" placeholder="Search users" [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event || '')" />
            </label>
            <p-select [options]="namespaceFilterOptions()" optionLabel="name" optionValue="id" [ngModel]="selectedNamespace()" (ngModelChange)="selectedNamespace.set($event)" ariaLabel="Filter by tenant" class="users-tenant-filter" appendTo="body" />
            <span class="management-results">{{ resultCount() }} shown</span>
        </div>
        <p-table class="management-table" [value]="filteredUsers()" [loading]="loading()" [paginator]="true" [rows]="10" [rowsPerPageOptions]="[10, 25, 50]" [rowHover]="true" [scrollable]="true">
            <ng-template #header>
                <tr>
                    <th pSortableColumn="displayName">User <p-sorticon field="displayName" /></th>
                    <th pSortableColumn="email" class="hidden md:table-cell">Email <p-sorticon field="email" /></th>
                    <th>Roles</th>
                    <th>Status</th>
                    <th><span class="sr-only">Details</span></th>
                </tr>
            </ng-template>
            <ng-template #body let-user>
                <!-- The row opens the drawer; the name and chevron buttons let the keyboard reach it -->
                <tr class="cursor-pointer" (click)="openDrawer(user)">
                    <td>
                        <div class="management-name">
                            <app-user-avatar [user]="user" />
                            <div>
                                <button type="button">{{ user.displayName || user.username }}</button>
                                <small>{{ user.username }}</small>
                            </div>
                        </div>
                    </td>
                    <td class="hidden md:table-cell">{{ user.email || '—' }}</td>
                    <td>
                        <div class="flex flex-wrap gap-1 min-w-56">
                            @if (user.superAdmin) {
                                <p-tag value="Super Admin" severity="danger" [rounded]="true" />
                            }
                            @if (user.serviceDesk) {
                                <p-tag value="Service desk" severity="warn" [rounded]="true" icon="pi pi-shield" />
                            }
                            @for (perm of user.permissions; track permNs(perm)) {
                                <app-role-badge [roleId]="permRoleId(perm)" [roleName]="permRoleName(perm)" [prefix]="namespaceName(permNs(perm))" />
                            }
                            @if (!user.superAdmin && !(user.permissions || []).length) {
                                <span class="text-sm text-color-secondary">No access</span>
                            }
                        </div>
                    </td>
                    <td>
                        @if (isPending(user)) {
                            <p-tag value="Invited" severity="warn" [rounded]="true" />
                        } @else if (user.disabled) {
                            <p-tag value="Disabled" severity="danger" [rounded]="true" />
                        } @else {
                            <p-tag value="Active" severity="success" [rounded]="true" />
                        }
                    </td>
                    <td>
                        <div class="management-row-actions">
                            <p-button icon="pi pi-chevron-right" [text]="true" [rounded]="true" [ariaLabel]="'Details for ' + (user.displayName || user.username)" pTooltip="Details" />
                        </div>
                    </td>
                </tr>
            </ng-template>
            <ng-template #emptymessage>
                <tr>
                    <td colspan="5">
                        <app-empty-state icon="pi pi-users" title="No users match the current filters" />
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </section>
</div>

<!-- Invite dialog (primary flow) -->
<p-dialog header="Invite User" [visible]="showInvite()" [modal]="true" [closable]="true" (visibleChange)="showInvite.set($event)" [style]="{ width: '28rem' }">
    <p class="text-sm text-color-secondary mb-3">They'll receive a sign-in link by email — no password needed.</p>
    <label class="block mb-1 font-medium" for="invite-email">Email</label>
    <input pInputText id="invite-email" [(ngModel)]="inviteEmail" placeholder="person@company.com" class="w-full mb-4" [disabled]="inviting()" />

    <label class="block mb-1 font-medium">Tenant access (optional)</label>
    <p-select
        [options]="namespaceOptions()"
        optionLabel="name"
        optionValue="id"
        [ngModel]="inviteNamespaceId()"
        (ngModelChange)="inviteNamespaceId.set($event)"
        placeholder="Select a tenant"
        [showClear]="true"
        class="w-full mb-3"
        appendTo="body" />
    @if (inviteNamespaceId()) {
    <label class="block mb-1 font-medium">Role</label>
    <p-select [options]="roleOptions()" optionLabel="name" optionValue="id" [ngModel]="inviteRoleId()" (ngModelChange)="inviteRoleId.set($event)" class="w-full mb-3" appendTo="body" />
    }

    <div class="flex justify-end gap-2 mt-3">
        <button pButton type="button" class="p-button-text" (click)="showInvite.set(false)" [disabled]="inviting()"><span class="p-button-label">Cancel</span></button>
        <button pButton type="button" (click)="submitInvite()" [loading]="inviting()" [disabled]="inviting() || !inviteEmail().trim()">@if (inviting()) {<i class="pi pi-spinner pi-spin p-button-loading-icon" aria-hidden="true"></i>} @else {<i class="pi pi-envelope p-button-icon" aria-hidden="true"></i>}<span class="p-button-label">Send Invite</span></button>
    </div>
</p-dialog>

<!-- Manual create dialog (secondary, superadmin) -->
<p-dialog header="Create User" [visible]="showCreate()" [modal]="true" [closable]="true" (visibleChange)="showCreate.set($event)" [style]="{ width: '28rem' }">
    <p class="text-sm text-color-secondary mb-3">Prefer inviting by email — use this only when the person can't receive sign-in links.</p>
    <label class="block mb-1 font-medium">Username</label>
    <input pInputText [ngModel]="createForm().username" (ngModelChange)="setCreateField('username', $event)" class="w-full mb-3" [disabled]="creating()" />
    <label class="block mb-1 font-medium">Email</label>
    <input pInputText [ngModel]="createForm().email" (ngModelChange)="setCreateField('email', $event)" class="w-full mb-3" [disabled]="creating()" />
    <label class="block mb-1 font-medium">Temporary Password</label>
    <input pInputText type="password" [ngModel]="createForm().password" (ngModelChange)="setCreateField('password', $event)" class="w-full mb-3" [disabled]="creating()" />
    <label class="block mb-1 font-medium">Display Name (optional)</label>
    <input pInputText [ngModel]="createForm().displayName" (ngModelChange)="setCreateField('displayName', $event)" class="w-full mb-3" [disabled]="creating()" />
    <div class="flex justify-end gap-2 mt-3">
        <button pButton type="button" class="p-button-text" (click)="showCreate.set(false)" [disabled]="creating()"><span class="p-button-label">Cancel</span></button>
        <button pButton type="button" (click)="submitCreate()" [loading]="creating()" [disabled]="creating() || !createForm().username || !createForm().password || !createForm().email">@if (creating()) {<i class="pi pi-spinner pi-spin p-button-loading-icon" aria-hidden="true"></i>}<span class="p-button-label">Create</span></button>
    </div>
</p-dialog>

<!-- User detail drawer -->
<p-drawer [visible]="drawerVisible()" (visibleChange)="drawerVisible.set($event)" position="right" [style]="{ width: 'min(30rem, 100vw)' }">
    <ng-template #header>
        @if (selectedUser(); as user) {
        <div class="flex items-center gap-3">
            <app-user-avatar [user]="user" size="large" />
            <div>
                <div class="text-lg font-semibold">{{ user.displayName || user.username }}</div>
                <div class="text-sm text-color-secondary">{{ user.email || user.username }}</div>
            </div>
        </div>
        }
    </ng-template>
    @if (selectedUser(); as user) {
    <div class="flex flex-wrap gap-2 mb-4">
        @if (user.superAdmin) {
        <p-tag value="Super Admin" severity="danger" [rounded]="true" />
        } @if (isPending(user)) {
        <p-tag value="Invite Pending" severity="warn" [rounded]="true" />
        } @else if (user.disabled) {
        <p-tag value="Disabled" severity="danger" [rounded]="true" />
        } @else {
        <p-tag value="Active" severity="success" [rounded]="true" />
        }
    </div>

    <h4 class="font-semibold mb-2">Tenant Roles</h4>
    @if (!(user.permissions || []).length) {
    <p class="text-sm text-color-secondary mb-3">No tenant access.</p>
    } @for (perm of user.permissions; track permNs(perm)) {
    <div class="flex items-center gap-2 mb-2">
        <span class="flex-1 truncate">{{ namespaceName(permNs(perm)) }}</span>
        @if (canEditRoles(user) && !isSelf(user)) {
        <p-select [options]="roleOptions()" optionLabel="name" optionValue="id" [ngModel]="permRoleId(perm)" (ngModelChange)="changeRole(user, permNs(perm), $event)" [disabled]="savingRole() === permNs(perm)" class="w-36" appendTo="body" />
        <button pButton iconOnly type="button" class="p-button-sm p-button-text p-button-danger" pTooltip="Remove access" (click)="revokeAccess(user, permNs(perm))"><i class="pi pi-trash p-button-icon" aria-hidden="true"></i></button>
        } @else {
        <app-role-badge [roleId]="permRoleId(perm)" [roleName]="permRoleName(perm)" />
        }
    </div>
    } @if (canEditRoles(user) && !isSelf(user) && grantableNamespaces().length) {
    <div class="flex items-center gap-2 mt-3">
        <p-select [options]="grantableNamespaces()" optionLabel="name" optionValue="id" [ngModel]="addGrantNamespaceId()" (ngModelChange)="addGrantNamespaceId.set($event)" placeholder="Add namespace..." class="flex-1" appendTo="body" />
        @if (addGrantNamespaceId()) {
        <p-select [options]="roleOptions()" optionLabel="name" optionValue="id" [ngModel]="addGrantRoleId()" (ngModelChange)="addGrantRoleId.set($event)" class="w-32" appendTo="body" />
        <button pButton iconOnly type="button" class="p-button-sm" (click)="addGrant()"><i class="pi pi-plus p-button-icon" aria-hidden="true"></i></button>
        }
    </div>
    }

    @if (isSuperAdmin() && !user.superAdmin) {
    <app-service-desk-grant [userId]="user.id" [namespaces]="namespaceOptions()" [memberOf]="selectedMemberOf()" (changed)="load()" />
    }

    <h4 class="font-semibold mt-5 mb-2">Account</h4>
    <div class="flex flex-col gap-2">
        @if (isPending(user)) {
        <button pButton type="button" class="p-button-outlined w-full" (click)="resendInvite(user)"><i class="pi pi-envelope p-button-icon" aria-hidden="true"></i><span class="p-button-label">Resend Invite</span></button>
        } @else if (user.email) {
        <button pButton type="button" class="p-button-outlined w-full" (click)="sendPasswordReset(user)"><i class="pi pi-envelope p-button-icon" aria-hidden="true"></i><span class="p-button-label">Send Password Reset</span></button>
        } @if (isSuperAdmin() && !isSelf(user)) {
        <button pButton type="button" class="p-button-outlined w-full" (click)="toggleSuperAdmin(user)"><i [class]="user.superAdmin ? 'pi pi-star-fill' : 'pi pi-star'" class="p-button-icon" aria-hidden="true"></i><span class="p-button-label">{{ user.superAdmin ? 'Remove Super Admin' : 'Make Super Admin' }}</span></button>
        <button pButton type="button" class="p-button-outlined w-full" (click)="toggleDisabled(user)"><i [class]="user.disabled ? 'pi pi-check-circle' : 'pi pi-ban'" class="p-button-icon" aria-hidden="true"></i><span class="p-button-label">{{ user.disabled ? 'Enable Account' : 'Disable Account' }}</span></button>
        <button pButton type="button" class="p-button-danger p-button-outlined w-full" (click)="deleteUser(user)"><i class="pi pi-trash p-button-icon" aria-hidden="true"></i><span class="p-button-label">Delete User</span></button>
        }
    </div>
    }
</p-drawer>
